// VideoTile: homogeneous video card.
//
// By default renders a BRANDED SVG (gradient + concentric rings + tag/title text).
// Never raw YouTube thumbnails (per the design brief: "strict, only branded SVGs").
//
// If a `customThumbnail` src is provided, that image replaces the SVG. Useful
// when you want a per-video uploaded artwork.
//
// Click → swaps the static tile for an inline YouTubeEmbed that plays in place.

import { useState } from 'react';
import { Play } from 'lucide-react';
import { YouTubeEmbed } from '@ui/library/embeds/YouTubeEmbed';
import { Card } from './Card';
import { cx } from '@lib/utils/classNames';
import type { Video } from '@lib/schemas/youtube';

// ---------------------------------------------------------------------------
// Branded SVG poster — same colour-system logic as PostThumbnail.
// ---------------------------------------------------------------------------

const CHANNEL_PALETTE: ReadonlyArray<readonly [string, string, string]> = [
  ['#1A2E5E', '#0B1E20', '#5BEFD0'], // mint accent
  ['#3D1F4E', '#0B1E20', '#FF85A3'], // soft pink
  ['#0B2638', '#0B1E20', '#14C7C3'], // teal
  ['#0B3326', '#0B1E20', '#57D092'], // green
  ['#4E1F2E', '#0B1E20', '#FF5079'], // magenta
  ['#1E2A56', '#0B1E20', '#93F7E4'], // bright mint
];

function paletteFor(seed: string): readonly [string, string, string] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return CHANNEL_PALETTE[h % CHANNEL_PALETTE.length]!;
}

interface BrandedPosterProps {
  video: Video;
}

function BrandedPoster({ video }: BrandedPosterProps) {
  const [start, end, accent] = paletteFor(video.channelTitle || video.videoId);
  const seed = Array.from(video.videoId).reduce(
    (acc, c) => (acc * 31 + c.charCodeAt(0)) >>> 0,
    0
  );
  const ringX = 380 + ((seed >> 2) % 120);
  const ringY = 60 + ((seed >> 6) % 80);
  const ringR = 90 + ((seed >> 10) % 40);

  return (
    <svg
      viewBox="0 0 600 338"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label={`${video.channelTitle} · ${video.title}`}
      style={{ display: 'block', width: '100%', height: '100%' }}
    >
      <defs>
        <linearGradient id={`vt-bg-${seed}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={start} />
          <stop offset="100%" stopColor={end} />
        </linearGradient>
        <radialGradient id={`vt-glow-${seed}`} cx="0.5" cy="0.5">
          <stop offset="0%" stopColor={accent} stopOpacity="0.40" />
          <stop offset="60%" stopColor={accent} stopOpacity="0.10" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width="600" height="338" fill={`url(#vt-bg-${seed})`} />
      <circle cx={ringX} cy={ringY} r={ringR * 2.5} fill={`url(#vt-glow-${seed})`} />
      {[1, 2, 3].map((i) => (
        <circle
          key={i}
          cx={ringX}
          cy={ringY}
          r={ringR * i}
          fill="none"
          stroke={accent}
          strokeOpacity={0.16 / i}
          strokeWidth={1}
        />
      ))}
      <line x1="0" y1="337.5" x2="600" y2="337.5" stroke={accent} strokeOpacity="0.20" />
      <text
        x="32"
        y="46"
        fill={accent}
        fontFamily="'Space Mono', ui-monospace, monospace"
        fontSize="11"
        letterSpacing="2.2"
        style={{ textTransform: 'uppercase' }}
      >
        {video.channelTitle.toUpperCase()}
      </text>
      <text
        x="32"
        y="310"
        fill="#B6D0CC"
        fontFamily="'Space Mono', ui-monospace, monospace"
        fontSize="10"
        letterSpacing="1.4"
        opacity="0.7"
      >
        {video.year} · {video.viewCount.toLocaleString()} VIEWS
      </text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Static poster — branded SVG OR custom image, with overlays.
// ---------------------------------------------------------------------------

interface StaticPosterProps {
  video: Video;
  customThumbnail?: string;
  onActivate: () => void;
  playLabel: string;
}

function StaticPoster({
  video,
  customThumbnail,
  onActivate,
  playLabel,
}: StaticPosterProps) {
  return (
    <button
      type="button"
      className="video-tile__poster"
      onClick={onActivate}
      aria-label={`${playLabel}: ${video.title}`}
    >
      <div className="video-tile__art">
        {customThumbnail ? (
          <img
            src={customThumbnail}
            alt=""
            loading="lazy"
            decoding="async"
            style={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <BrandedPoster video={video} />
        )}
      </div>
      <div className="video-tile__overlay" aria-hidden="true">
        <div className="video-tile__play">
          <Play size={28} fill="currentColor" />
        </div>
        <span className="video-tile__duration">{video.durationLabel}</span>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// VideoTile — public component
// ---------------------------------------------------------------------------

export interface VideoTileProps {
  video: Video;
  /** Optional override image (path or URL). When omitted, the branded SVG is used. */
  customThumbnail?: string;
  /** Stagger animation index (for entry reveal). */
  staggerIndex?: number;
  /** Accessible label prefix for the play button — e.g. "Play". */
  playLabel?: string;
}

export function VideoTile({
  video,
  customThumbnail,
  staggerIndex,
  playLabel = 'Play video',
}: VideoTileProps) {
  const [active, setActive] = useState(false);

  return (
    <Card
      variant="talkcard"
      staggerIndex={staggerIndex}
      className={cx('video-tile', active && 'video-tile--active')}
    >
      <div className="video-tile__media">
        {active ? (
          <YouTubeEmbed id={video.videoId} title={video.title} />
        ) : (
          <StaticPoster
            video={video}
            {...(customThumbnail ? { customThumbnail } : {})}
            onActivate={() => setActive(true)}
            playLabel={playLabel}
          />
        )}
      </div>
      <div className="video-tile__body">
        <span className="video-tile__channel">{video.channelTitle}</span>
        <h4 className="video-tile__title">{video.title}</h4>
        <div className="video-tile__meta">
          <span>{video.year}</span>
          <span className="dot" />
          <span>{video.viewCount.toLocaleString()} views</span>
        </div>
      </div>
    </Card>
  );
}
